import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

import { api, ApiError, type ParameterVersionDetail } from "@/api/client";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * B3/V1 — "see values": one version against the ACTIVE set (via GET /api/parameters/{id}, B2),
 * rendered in a modal dialog. Parameters are grouped by their schema_version-2 block — the same
 * families as the Settings panels — divergent fields are highlighted, and when comparing the
 * default view shows differences only. Closes the "blind activation" hole (étude §4): the
 * operator reads the actual numbers before applying anything.
 *
 * Honest degraded states: no active pointer (SYSTEM default runs) → single column + an explicit
 * note; the selected version IS the active one → single column, said as such; fetch/parse
 * failure → explicit error, never a silent empty diff.
 */

/** Ordre d'affichage = choix UX (échelle de stops d'abord), PAS l'ordre du codec.
 *  Les blocs morts du schema 13 (defensive, close_classification) ne sont plus
 *  nommés — un vieux set incompatible qui les porte encore tombe dans « Autres »
 *  (rendu générique, jamais une erreur). */
const BLOCK_ORDER = [
  "stop",
  "sizing",
  "risk",
  "limits",
  "algo",
  "stream",
  "live_safety",
  "margin",
];

const BLOCK_TITLE: Record<string, string> = {
  stop: "Échelle de stops",
  sizing: "Taille de position",
  risk: "Risque",
  limits: "Caps de positions",
  algo: "Algorithme",
  stream: "Flux",
  live_safety: "Sécurité LIVE",
  margin: "Marge",
  other: "Autres (blocs hors schéma courant)",
};

export default function ParamValuesDiff({
  candidateId,
  activeId,
  onClose,
}: {
  candidateId: number;
  /** The ACTIVE_PARAM_SET_ID pointer target (null → the compiled SYSTEM default runs). */
  activeId: number | null;
  onClose: () => void;
}) {
  const [candidate, setCandidate] = useState<ParameterVersionDetail | null>(null);
  const [active, setActive] = useState<ParameterVersionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const needsActive = activeId !== null && activeId !== candidateId;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetches: Promise<void>[] = [
      api.parameterById(candidateId).then((d) => {
        if (!cancelled) setCandidate(d);
      }),
    ];
    if (needsActive && activeId !== null) {
      fetches.push(
        api.parameterById(activeId).then((d) => {
          if (!cancelled) setActive(d);
        }),
      );
    }
    Promise.all(fetches)
      .catch((e) => {
        if (!cancelled) {
          setError(
            e instanceof ApiError && e.status === 404
              ? "Version introuvable."
              : "Impossible de charger les valeurs de la version.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [candidateId, activeId, needsActive]);

  const candidateGroups = useMemo(
    () => (candidate ? safeGroup(candidate.paramsJson) : null),
    [candidate],
  );
  const activeGroups = useMemo(
    () => (needsActive && active ? safeGroup(active.paramsJson) : null),
    [needsActive, active],
  );
  const parseFailed =
    (candidate && candidateGroups === null) || (needsActive && active && activeGroups === null);

  const twoCols = activeGroups !== null && activeId !== null;
  const blocks = useMemo(
    () => (candidateGroups ? buildBlocks(candidateGroups, activeGroups) : []),
    [candidateGroups, activeGroups],
  );
  const totalFields = blocks.reduce((n, b) => n + b.fields.length, 0);
  const totalDivergent = blocks.reduce((n, b) => n + b.divergentCount, 0);
  const diffOnly = twoCols && totalDivergent > 0 && !showAll;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        data-testid="param-values-diff"
        className="gap-3 sm:max-w-2xl"
      >
        <DialogHeader className="gap-1">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-sm">
              Valeurs — version #{candidateId}
              {twoCols ? ` vs active #${activeId}` : ""}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground rounded-md p-0.5"
              aria-label="Fermer la vue des valeurs"
              data-testid="param-values-close"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
          <DialogDescription className="text-xs">
            {activeId === null && (
              <span data-testid="param-values-no-active">
                Pas de pointeur actif — le défaut SYSTEM compilé tourne ; seules les
                valeurs de cette version sont affichées.
              </span>
            )}
            {activeId === candidateId && (
              <span data-testid="param-values-is-active">
                Cette version EST le set actif — ses valeurs sont affichées.
              </span>
            )}
            {twoCols && !loading && !error && !parseFailed && (
              <span data-testid="param-values-summary">
                {totalDivergent === 0
                  ? `Aucune divergence — les ${totalFields} paramètres sont identiques.`
                  : `${totalDivergent} paramètres sur ${totalFields} divergent.`}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && <span className="text-muted-foreground text-xs">Chargement des valeurs…</span>}
        {error && (
          <span className="text-loss text-xs" role="alert" data-testid="param-values-error">
            {error}
          </span>
        )}
        {parseFailed && !error && (
          <span className="text-loss text-xs" role="alert" data-testid="param-values-error">
            params_json illisible — refus de rendre un diff possiblement faux.
          </span>
        )}

        {!loading && !error && candidateGroups && !parseFailed && (
          <>
            {twoCols && totalDivergent > 0 && (
              <button
                type="button"
                onClick={() => setShowAll((s) => !s)}
                className="text-muted-foreground hover:bg-accent hover:text-foreground w-fit rounded-md border px-2 py-0.5 text-xs font-medium transition-colors"
                data-testid="param-values-toggle"
              >
                {diffOnly ? `Tout afficher (${totalFields})` : "Divergences seules"}
              </button>
            )}
            <div className="overflow-y-auto" style={{ maxHeight: "60vh" }}>
              <GroupedTable
                blocks={blocks}
                candidateId={candidateId}
                activeId={twoCols ? activeId : null}
                twoCols={twoCols}
                diffOnly={diffOnly}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface BlockFields {
  block: string;
  title: string;
  fields: Array<{
    /** Flat key — `block.field` for nested fields, the raw key for root scalars. */
    flatKey: string;
    field: string;
    a: string | undefined;
    b: string | undefined;
    diverges: boolean;
  }>;
  divergentCount: number;
}

function GroupedTable({
  blocks,
  candidateId,
  activeId,
  twoCols,
  diffOnly,
}: {
  blocks: BlockFields[];
  candidateId: number;
  activeId: number | null;
  twoCols: boolean;
  diffOnly: boolean;
}) {
  const visible = diffOnly
    ? blocks
        .filter((b) => b.divergentCount > 0)
        .map((b) => ({ ...b, fields: b.fields.filter((f) => f.diverges) }))
    : blocks;
  return (
    <table className="w-full text-left text-xs" data-testid="param-values-table">
      <thead className="text-muted-foreground">
        <tr>
          <th className="py-0.5 pr-3 font-medium">paramètre</th>
          <th className="py-0.5 pr-3 font-medium">#{candidateId}</th>
          {twoCols && <th className="py-0.5 pr-3 font-medium">#{activeId} (actif)</th>}
        </tr>
      </thead>
      <tbody>
        {visible.map((b) => (
          <BlockRows key={b.block} block={b} twoCols={twoCols} />
        ))}
      </tbody>
    </table>
  );
}

function BlockRows({ block, twoCols }: { block: BlockFields; twoCols: boolean }) {
  return (
    <>
      <tr data-testid={`param-values-block-${block.block}`}>
        <th
          colSpan={twoCols ? 3 : 2}
          className="text-foreground border-border border-t pt-2 pb-1 pr-3 text-left text-[0.68rem] font-semibold tracking-wide uppercase"
        >
          {block.title}
          {twoCols && block.divergentCount > 0 && (
            <span className="text-warning ml-2 font-mono normal-case">
              {block.divergentCount} diff
            </span>
          )}
        </th>
      </tr>
      {block.fields.map((f) => (
        <tr
          key={f.flatKey}
          className={cn("border-border/30 border-t", f.diverges && "bg-warning/10 font-semibold")}
          data-testid={f.diverges ? `param-diff-${f.flatKey}` : undefined}
        >
          <td className="py-0.5 pr-3" title={f.flatKey}>
            <FieldLabel field={f.field} />
          </td>
          <td className="py-0.5 pr-3 font-mono tabular-nums">{f.a ?? "—"}</td>
          {twoCols && <td className="py-0.5 pr-3 font-mono tabular-nums">{f.b ?? "—"}</td>}
        </tr>
      ))}
    </>
  );
}

/** Mechanical unit suffixes — derived from the field name, zero per-field map to maintain. */
const UNIT_SUFFIXES: Array<[suffix: string, unit: string]> = [
  ["_eur_per_point", "€/pt"],
  ["_points", "pts"],
  ["_eur", "€"],
  ["_pct", "%"],
  ["_hours", "h"],
  ["_seconds", "s"],
  ["_ms", "ms"],
];

function FieldLabel({ field }: { field: string }) {
  let base = field;
  let unit: string | null = null;
  for (const [suffix, u] of UNIT_SUFFIXES) {
    if (base.endsWith(suffix)) {
      unit = u;
      base = base.slice(0, -suffix.length);
      break;
    }
  }
  return (
    <span>
      {base.replaceAll("_", " ")}
      {unit && <span className="text-muted-foreground"> ({unit})</span>}
    </span>
  );
}

/** Order blocks per BLOCK_ORDER (unknown blocks after, alphabetically; "other" last) and merge
 *  the candidate/active field sets — candidate field order first, active-only fields appended. */
function buildBlocks(
  candidateGroups: Record<string, Record<string, string>>,
  activeGroups: Record<string, Record<string, string>> | null,
): BlockFields[] {
  const names = new Set([
    ...Object.keys(candidateGroups),
    ...(activeGroups ? Object.keys(activeGroups) : []),
  ]);
  const known = BLOCK_ORDER.filter((b) => names.has(b));
  const unknown = [...names]
    .filter((b) => !BLOCK_ORDER.includes(b) && b !== "other")
    .sort();
  const ordered = [...known, ...unknown, ...(names.has("other") ? ["other"] : [])];

  return ordered.map((block) => {
    const a = candidateGroups[block] ?? {};
    const b = activeGroups?.[block] ?? {};
    const fieldNames = [...Object.keys(a), ...Object.keys(b).filter((k) => !(k in a))];
    const fields = fieldNames.map((field) => {
      const av = a[field];
      const bv = activeGroups ? b[field] : undefined;
      const diverges = activeGroups !== null && !sameValue(av, bv);
      return {
        flatKey: block === "other" ? field : `${block}.${field}`,
        field,
        a: av,
        b: bv,
        diverges,
      };
    });
    return {
      block,
      title: BLOCK_TITLE[block] ?? block.replaceAll("_", " "),
      fields,
      divergentCount: fields.filter((f) => f.diverges).length,
    };
  });
}

/** Parse the canonical schema_version-2 document to block → field → display value; root scalars
 *  land under "other"; null on parse failure. */
export function safeGroup(
  paramsJson: string,
): Record<string, Record<string, string>> | null {
  try {
    const root = JSON.parse(paramsJson) as Record<string, unknown>;
    if (root === null || typeof root !== "object") return null;
    const out: Record<string, Record<string, string>> = {};
    for (const [block, v] of Object.entries(root)) {
      if (block === "schema_version") continue;
      if (v !== null && typeof v === "object" && !Array.isArray(v)) {
        const fields: Record<string, string> = {};
        for (const [field, val] of Object.entries(v as Record<string, unknown>)) {
          fields[field] = display(val);
        }
        out[block] = fields;
      } else {
        (out.other ??= {})[block] = display(v);
      }
    }
    return out;
  } catch {
    return null;
  }
}

function display(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return JSON.stringify(v);
  return String(v);
}

/** Scale-blind for numbers ("1.0" ≡ "1"); exact string compare otherwise. */
function sameValue(a: string | undefined, b: string | undefined): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  return false;
}
